import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import { concat, fn, hash } from '@ember/helper';
import Component from '@glimmer/component';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import { t } from 'ember-intl';
import and from 'ember-truth-helpers/helpers/and';
import eq from 'ember-truth-helpers/helpers/eq';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import BvCollectionLoader from 'volta/components/bv-collection-loader';
import BvEmptySearchResult from 'volta/components/bv-empty-search-result';
import BvTask from 'volta/components/bv-task';
import BvText from 'volta/components/bv-text';
import TasksKitSubmitBtn from 'volta/components/tasks-kit/submit-btn';
import classNames from 'volta/helpers/class-names';
import isTrue from 'volta/helpers/is-true';
import { TaskResourceName } from 'volta/models/task';
import autoanimate from 'volta/modifiers/autoanimate';
import lowerCase from 'lodash/lowerCase';
import type { IResourceName } from 'volta/models/base-model';
import type { TDate } from 'volta/models/types/calendar';
import type { TTaskType, UTask } from 'volta/models/types/tasks';
import type { TSelectedView } from '../';
import type { TEntityType } from 'volta/models/types/entity-types';
export interface TasksKitListSignature {
    Args: {
        authorizedUsers?: string[];
        disabled?: boolean;
        dueDateIdPrefix?: string;
        entityResourceName?: IResourceName;
        hideAddButton?: boolean;
        isAutoResolvable?: boolean;
        isLoading?: boolean;
        newDisabled?: boolean;
        onDelete?: (task: UTask) => void;
        onDraft?: (date: TDate) => void;
        onResolve?: (task: UTask) => void;
        onSelect?: (task: UTask) => void;
        onSubmit?: (task: UTask) => void;
        readonlyTasks?: boolean;
        selectedTask?: UTask;
        selectedView?: TSelectedView;
        showTaskType?: boolean;
        tasks: Record<string, UTask[]>;
        taskType?: TTaskType;
        taskWrapperClass?: string;
        onLightBg?: boolean;
    };
    Blocks: {
        default: [string, UTask[], number];
        taskGroupHeader: [string, UTask[], number];
    };
    Element: HTMLDivElement;
}
export type TCommonListArgs = Pick<TasksKitListSignature['Args'], 'authorizedUsers' | 'dueDateIdPrefix' | 'entityResourceName' | 'hideAddButton' | 'isAutoResolvable' | 'isLoading' | 'newDisabled' | 'onDelete' | 'onDraft' | 'onResolve' | 'onSelect' | 'onSubmit' | 'readonlyTasks' | 'selectedTask' | 'selectedView' | 'tasks' | 'taskType' | 'taskWrapperClass'>;
export const getEntityIdFromGroupId = (formattedLabel: string)=>{
    return formattedLabel.split('|').pop();
};
export const getEntityFromGroupId = (entities: Record<string, {
    linkedEntityType: TEntityType;
}>, groupId: string)=>{
    const entityId = getEntityIdFromGroupId(groupId);
    return entityId ? entities[entityId] : undefined;
};
export default class TasksKitListComponent extends Component<TasksKitListSignature> {
    get tasks() {
        return Object.entries(this.args.tasks).map(([groupId, value])=>{
            return {
                groupId,
                tasks: value
            };
        });
    }
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    {{#if @isLoading}}
      <BvCollectionLoader @resourceName={{TaskResourceName}} />
    {{/if}}
    {{#each this.tasks key='groupId' as |group index|}}
      <div id={{concat @dueDateIdPrefix group.groupId}} class='u-scroll-margin-top' ...attributes>
        {{#if (has-block)}}
          {{yield group.groupId group.tasks index}}
        {{else}}
          <div
            class='bv-task-group-divider
              {{if (isTrue @selectedTask) "bv-task-group-divider--selected"}}'
          >
            <BvText
              @as='h3'
              @variant='heading-xs'
              @text={{group.groupId}}
              class='bv-task-group-title u-text-upper'
            />
          </div>
        {{/if}}
        {{#if group.tasks.length}}
          <div class={{classNames 'u-py-2' @taskWrapperClass}}>
            {{#if (has-block 'taskGroupHeader')}}
              {{yield group.groupId group.tasks index to='taskGroupHeader'}}
            {{/if}}
            <div class={{if (has-block 'taskGroupHeader') 'u-pl-4'}} {{autoanimate true}}>
              {{#each group.tasks key='id' as |task|}}
                <BvTask
                  @task={{task}}
                  @isSelected={{eq @selectedTask.id task.id}}
                  @disabled={{or @readonlyTasks @disabled}}
                  @isResolvable={{true}}
                  @isDue={{true}}
                  @isAssignable={{true}}
                  {{! @glint-ignore}}
                  @onSubmit={{@onSubmit}}
                  {{! @glint-ignore}}
                  @onResolve={{@onResolve}}
                  {{! @glint-ignore}}
                  @onDelete={{@onDelete}}
                  {{! @glint-ignore}}
                  @onSelect={{@onSelect}}
                  @isAutoResolvable={{@isAutoResolvable}}
                  @taskType={{@taskType}}
                  @resourceName={{or
                    @entityResourceName
                    (hash plural=task.linkedEntityType singular=task.linkedEntityType)
                  }}
                  @showTaskType={{@showTaskType}}
                  @authorizedUsers={{@authorizedUsers}}
                  @onLightBg={{@onLightBg}}
                />
              {{/each}}
            </div>
            {{#if
              (and (eq @selectedView 'open') (not @readonlyTasks) (not @hideAddButton) @onDraft)
            }}
              <TasksKitSubmitBtn
                @hideSpinner={{true}}
                @onClick={{fn (optional @onDraft) group.groupId}}
                @disabled={{@newDisabled}}
                @fullWidth={{true}}
                @subtle={{true}}
                class='u-mx-auto u-mt-2'
              />
            {{/if}}
          </div>
        {{/if}}
      </div>
    {{else}}
      <BvEmptySearchResult
        class='u-p-5'
        @title={{t
          'bvList.emptySearchResultTitle'
          resourceNamePlural=(lowerCase (t TaskResourceName.plural))
        }}
      />
    {{/each}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        'TasksKit::List': typeof TasksKitListComponent;
        'tasks-kit/list': typeof TasksKitListComponent;
    }
}
